import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  styled,
  Typography,
} from '@mui/material';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { IEmployee } from 'src/@types/apiResponseTypes';
import { departmentApi, employeeApi } from 'src/api';
import DialogTitleWithCloseButton from 'src/components/dialog/DialogTitleWithCloseButton';
import { RHFAutocomplete } from 'src/components/hook-form';
import { IDepartmentModelSnapshot } from 'src/models/department/Department';

import { CheckboxTree } from './custom-tree-view';

interface IEmployeeWithDepartment extends IEmployee {
  deptLabel: string;
  deptPath: string[]; // 본부 > 부서 경로
}

interface RHFEmployeeSelectProps {
  name: string;
  label: string;
  disabled?: boolean;
  readOnly?: boolean;
  returnType?: 'object' | 'number';
  multiple?: boolean; // default: true
  size?: 'small' | 'medium'; // default: 'medium'
  withResigned?: boolean; // 퇴사자 포함 여부: true면 필터 없이, false면 tmntYn=false
  /** multiple=false여도 항상 배열로 반환 */
  alwaysArray?: boolean;
}

/**
 * ## RHFEmployeeSelect 설명
 * - 직원 목록을 조회하여 선택된 userSid를 form에 적용하는 컴포넌트
 * - CEO 등급과 퇴사한 직원은 제외하고 조회
 * - 본부 > 부서 > 직원 계층 구조로 트리뷰 구성
 * - 부서 레이블에 본부까지 포함된 전체 경로 표시
 */
function RHFEmployeeSelect({
  name,
  label,
  disabled,
  readOnly,
  returnType = 'object',
  multiple = true,
  size = 'medium',
  withResigned = false,
  alwaysArray = false,
}: RHFEmployeeSelectProps) {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const { setValue, getValues } = useFormContext();
  // 부서연결 레이블이 추가된 직원 리스트 state
  const [employees, setEmployees] = useState<IEmployeeWithDepartment[]>([]);
  //  선택된 직원 리스트 state
  const [selectedEmployees, setSelectedEmployees] = useState<IEmployeeWithDepartment[]>([]);
  // 선택된 직원 아이디 리스트 state
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  // 직원 조회 모달 오픈 state
  const [openEmployeeModal, setOpenEmployees] = useState(false);
  // 직원 treeview 아이템 리스트 state
  const [employeeTreeItems, setEmployeeTreeItems] = useState<TreeViewBaseItem[]>([]);
  // 초기화 처리를 위한 state
  const [initialized, setInitialized] = useState(false);

  // setup initial data
  const setupInitialData = useCallback(async () => {
    try {
      // 직원 목록과 부서 목록을 병렬로 조회
      const [employeesResult, departmentsResult] = await Promise.all([
        // withResigned: 포함 시 파라미터 없이, 미포함 시 tmntYn=false
        employeeApi.getEmployees(withResigned ? undefined : { tmntYn: 'false' }),
        departmentApi.getDepartments(),
      ]);

      if (employeesResult?.data && departmentsResult?.data) {
        // 부서 계층 구조 생성
        type DeptNode = IDepartmentModelSnapshot & { children: DeptNode[] };
        const createDeptHierarchy = (depts: IDepartmentModelSnapshot[]) => {
          const deptMap = new Map<number, DeptNode>();
          const rootDepts: DeptNode[] = [];

          // 모든 부서를 Map에 저장
          depts.forEach((dept) => {
            const node: DeptNode = { ...dept, children: [] } as DeptNode;
            deptMap.set(dept.deptSid, node);
          });

          // 계층 구조 생성
          depts.forEach((dept) => {
            const deptNode = deptMap.get(dept.deptSid);
            if (!deptNode) return;
            if (dept.deptPsid && deptMap.has(dept.deptPsid)) {
              const parent = deptMap.get(dept.deptPsid)!;
              parent.children.push(deptNode);
            } else {
              rootDepts.push(deptNode);
            }
          });

          return { deptMap, rootDepts };
        };

        // 최상단 CEO 부서 제거
        const filteredDepartments: IDepartmentModelSnapshot[] = (
          departmentsResult.data || []
        ).filter((d) => !(d.deptPsid === null && d.deptNm === 'CEO'));
        const { deptMap, rootDepts } = createDeptHierarchy(filteredDepartments);

        // 부서 경로 생성 함수
        const getDeptPath = (deptSid: number, path: string[] = []): string[] => {
          const dept = deptMap.get(deptSid);
          if (!dept) return path;

          const currentPath = [dept.deptNm, ...path];
          if (dept.deptPsid) {
            return getDeptPath(dept.deptPsid, currentPath);
          }
          return currentPath;
        };

        // 직원 데이터에 부서 레이블과 경로 추가 (부서 경로에서 최상단 CEO 명칭 제거)
        const employeesWithDept = (employeesResult.data || []).map((emp) => {
          const deptPath = getDeptPath(emp.deptSid);
          const cleanedPath = deptPath.filter((nm, idx) => !(idx === 0 && nm === 'CEO'));
          return {
            ...emp,
            deptLabel: cleanedPath.join(' > '),
            deptPath: cleanedPath,
          };
        });

        setEmployees(employeesWithDept);

        // 트리뷰 데이터 생성 (본부 > 부서 > 직원)
        const createTreeFromHierarchy = (deptNode: DeptNode): TreeViewBaseItem => {
          const node: TreeViewBaseItem = {
            id: `dept-${deptNode.deptSid}`,
            label: deptNode.deptNm,
            children: [],
          };

          // 하위 부서 추가
          if (deptNode.children && deptNode.children.length > 0) {
            deptNode.children.forEach((child) => {
              node.children?.push(createTreeFromHierarchy(child));
            });
          }

          // 해당 부서의 직원들 추가
          const deptEmployees = employeesWithDept.filter((emp) => emp.deptSid === deptNode.deptSid);
          deptEmployees.forEach((emp) => {
            node.children?.push({
              id: emp.userSid.toString(),
              label: `${emp.empNm}${emp.tmntYn ? ' (퇴사)' : ''}`,
            });
          });

          return node;
        };

        const treeItems: TreeViewBaseItem[] = rootDepts.map((rootDept) =>
          createTreeFromHierarchy(rootDept),
        );

        setEmployeeTreeItems(treeItems);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    setupInitialData();
  }, [setupInitialData]);

  // 초기에 이미 선택된 직원 적용 (콜백)
  const applyInitialSelection = useCallback(() => {
    const rawValue = getValues(name) as unknown;
    if (!employees || employees.length === 0) return;

    if (Array.isArray(rawValue) && rawValue.length > 0) {
      let selectedUserSids: number[] = [];

      if (rawValue.every((v) => typeof v === 'number')) {
        selectedUserSids = rawValue as number[];
      } else {
        selectedUserSids = (rawValue as Array<{ userSid?: number }>)
          .map((v) => (typeof v === 'object' && v ? v.userSid : undefined))
          .filter((n): n is number => typeof n === 'number');
      }

      if (selectedUserSids.length > 0) {
        const initiallySelected = employees.filter((emp) => selectedUserSids.includes(emp.userSid));
        const nextSelected = multiple ? initiallySelected : initiallySelected.slice(0, 1);
        setSelectedEmployees(nextSelected);
        setSelectedItemIds(nextSelected.map((e) => String(e.userSid)));
      }
    } else if (rawValue && typeof rawValue === 'number') {
      const initiallySelected = employees.filter((emp) => emp.userSid === rawValue);
      const nextSelected = multiple ? initiallySelected : initiallySelected.slice(0, 1);
      setSelectedEmployees(nextSelected);
      setSelectedItemIds(nextSelected.map((e) => String(e.userSid)));
    } else {
      setSelectedEmployees(multiple ? [] : []);
      setSelectedItemIds([]);
    }
    setInitialized(true);
  }, [employees, getValues, name, setSelectedEmployees, setSelectedItemIds, multiple]);

  // 초기 선택 적용 트리거
  useEffect(() => {
    if (employees.length > 0 && !initialized) {
      applyInitialSelection();
    }
  }, [employees, initialized, applyInitialSelection]);

  // 직원  변경될 때 form에 members 정보 업데이트
  useEffect(() => {
    if (initialized) {
      let nextValue: unknown;

      if (multiple || alwaysArray) {
        // multiple이거나 alwaysArray면 항상 배열 반환
        nextValue = selectedEmployees.map((member) =>
          returnType === 'object' ? { userSid: member.userSid } : member.userSid,
        );
      } else {
        // 단일 선택
        nextValue =
          selectedEmployees.length > 0
            ? returnType === 'object'
              ? { userSid: selectedEmployees[0].userSid }
              : selectedEmployees[0].userSid
            : null;
      }

      setValue(name, nextValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [initialized, name, returnType, selectedEmployees, setValue, multiple, alwaysArray]);

  // 조회 모달에서 적용하기 클릭 시 처리 로직
  const handleApply = (selectedUserSids: number[]) => {
    const selected = employees.filter((emp) => selectedUserSids.includes(emp.userSid));
    setSelectedEmployees(multiple ? selected : selected.slice(0, 1));
    setOpenEmployees(false);
  };

  // 직원 조회 모달 닫기
  const onClose = () => {
    setOpenEmployees(false);
  };

  return (
    <Container>
      {/* 직원 Auto select */}
      <RHFAutocomplete
        name={name}
        label={label}
        options={employees}
        multiple={multiple}
        size={size}
        disableCloseOnSelect={multiple}
        value={multiple ? selectedEmployees : selectedEmployees[0] ?? null}
        onChange={(_, newValue) => {
          if (multiple) {
            setSelectedEmployees(newValue as IEmployeeWithDepartment[]);
          } else {
            setSelectedEmployees(newValue ? [newValue as IEmployeeWithDepartment] : []);
          }
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              label={`${option.empNm}${option.tmntYn ? ' (퇴사)' : ''}`}
              {...getTagProps({ index })}
              key={index}
            />
          ))
        }
        getOptionLabel={(option) => `${option.empNm}${option.tmntYn ? ' (퇴사)' : ''}`}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.userSid}>
            {multiple && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            <StyledStrongText>
              {`${option.empNm}${option.tmntYn ? ' (퇴사)' : ''}`}
            </StyledStrongText>
            &nbsp;
            <StyledText>{option.deptLabel}</StyledText>
          </li>
        )}
        sx={{ flexGrow: 1 }}
        disabled={disabled}
        readOnly={readOnly}
      />
      <StyledButton
        variant="outlined"
        onClick={() => setOpenEmployees(true)}
        disabled={disabled || readOnly}
        sx={{ height: size === 'small' ? 40 : 56 }}
      >
        조회
      </StyledButton>
      {/* 직원 조회 모달 */}
      <Dialog fullWidth maxWidth="sm" open={openEmployeeModal} onClose={onClose}>
        <DialogTitleWithCloseButton title={'직원 조회'} onClose={onClose} />
        <DialogContent>
          <CheckboxTree
            items={employeeTreeItems}
            selectedItemIds={selectedItemIds}
            setSelectedItemIds={setSelectedItemIds}
            defaultExpandedItems={employeeTreeItems.length > 0 ? [employeeTreeItems[0].id] : []}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="contained"
            onClick={() => handleApply(selectedItemIds.map((id) => Number(id)))}
          >
            적용하기
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default RHFEmployeeSelect;

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  width: '100%',
}));

const StyledText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 12,
}));

const StyledButton = styled(Button)(() => ({
  height: 56,
}));

const StyledStrongText = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  flexShrink: 0,
}));
